import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  API_BASE,
  api,
} from "../api";

import ChildrensBookManuscriptReadOnly from "../components/ChildrensBookManuscriptReadOnly";


function clean(value) {
  return String(
    value ?? ""
  ).trim();
}


function workIdFromLocation() {
  if (
    typeof window ===
    "undefined"
  ) {
    return "";
  }

  const path =
    String(
      window.location.pathname ||
      ""
    );

  const match =
    path.match(
      /^\/reader\/childrens-book\/([a-fA-F0-9]{24})\/?$/
    );

  return match
    ? match[1]
    : "";
}


function readerAssetUrl(
  workId,
  assetId
) {
  const cleanWorkId =
    clean(workId);

  const cleanAssetId =
    clean(assetId);

  if (
    !cleanWorkId ||
    !cleanAssetId
  ) {
    return "";
  }

  return `${API_BASE}/api/reader/childrens-book/${encodeURIComponent(
    cleanWorkId
  )}/assets/${encodeURIComponent(
    cleanAssetId
  )}`;
}


function readerBackCoverUrl(
  workId
) {
  const cleanWorkId =
    clean(workId);

  if (!cleanWorkId) {
    return "";
  }

  return `${API_BASE}/api/reader/childrens-book/${encodeURIComponent(
    cleanWorkId
  )}/back-cover`;
}


function unitPageLabels(
  units
) {
  let page = 1;

  return (
    Array.isArray(units)
      ? units
      : []
  ).map(
    (unit) => {
      const span =
        Number(
          unit?.pageSpan
        ) === 2
          ? 2
          : 1;

      const start =
        page;

      const end =
        page +
        span -
        1;

      page =
        end + 1;

      return span === 2
        ? `Pages ${start}–${end}`
        : `Page ${start}`;
    }
  );
}


function formattedDate(
  value
) {
  const source =
    clean(value);

  if (!source) {
    return "";
  }

  const date =
    new Date(source);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  return date.toLocaleDateString(
    undefined,
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );
}


export default function ChildrensBookReader({
  onNavigatePage,
}) {
  const workId =
    useMemo(
      () =>
        workIdFromLocation(),
      []
    );

  const [
    reader,
    setReader,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    activeIndex,
    setActiveIndex,
  ] = useState(0);

  const [
    imageUrl,
    setImageUrl,
  ] = useState("");

  const [
    imageLoading,
    setImageLoading,
  ] = useState(false);

  const [
    imageError,
    setImageError,
  ] = useState("");

  const [
    backCoverUrl,
    setBackCoverUrl,
  ] = useState("");

  const [
    backCoverAvailable,
    setBackCoverAvailable,
  ] = useState(false);

  useEffect(
    () => {
      let cancelled =
        false;

      async function load() {
        if (!workId) {
          setError(
            "This published book link is invalid."
          );

          setLoading(
            false
          );

          return;
        }

        try {
          const response =
            await api.get(
              `/api/reader/childrens-book/${encodeURIComponent(
                workId
              )}`
            );

          if (
            cancelled
          ) {
            return;
          }

          if (
            !response?.reader
          ) {
            throw new Error(
              "Published book is not available."
            );
          }

          setReader(
            response.reader
          );

          setError("");
        } catch (loadError) {
          if (
            cancelled
          ) {
            return;
          }

          setReader(null);

          setError(
            clean(
              loadError?.message
            ) ||
            "Published book is not available."
          );
        } finally {
          if (
            !cancelled
          ) {
            setLoading(
              false
            );
          }
        }
      }

      load();

      return () => {
        cancelled =
          true;
      };
    },
    [workId]
  );

  useEffect(
    () => {
      let cancelled =
        false;

      let objectUrl =
        "";

      async function loadBackCover() {
        setBackCoverUrl("");
        setBackCoverAvailable(
          false
        );

        if (
          !reader ||
          !workId
        ) {
          return;
        }

        try {
          const response =
            await fetch(
              readerBackCoverUrl(
                workId
              ),
              {
                method:
                  "GET",

                credentials:
                  "include",

                headers: {
                  Accept:
                    "application/pdf",
                },
              }
            );

          if (!response.ok) {
            return;
          }

          const contentType =
            clean(
              response.headers.get(
                "content-type"
              )
            ).toLowerCase();

          if (
            !contentType.startsWith(
              "application/pdf"
            )
          ) {
            return;
          }

          const blob =
            await response.blob();

          if (cancelled) {
            return;
          }

          objectUrl =
            URL.createObjectURL(
              blob
            );

          setBackCoverUrl(
            objectUrl
          );

          setBackCoverAvailable(
            true
          );
        } catch {
          /*
           * Historical published books may not have this
           * generated Reader derivative yet.
           */
        }
      }

      loadBackCover();

      return () => {
        cancelled =
          true;

        if (
          objectUrl &&
          typeof URL !==
            "undefined" &&
          typeof URL
            .revokeObjectURL ===
            "function"
        ) {
          URL.revokeObjectURL(
            objectUrl
          );
        }
      };
    },
    [
      reader,
      workId,
    ]
  );


  const units =
    useMemo(
      () =>
        Array.isArray(
          reader?.units
        )
          ? reader.units
          : [],
      [reader]
    );

  const pageLabels =
    useMemo(
      () =>
        unitPageLabels(
          units
        ),
      [units]
    );

  const items =
    useMemo(
      () => [
        {
          kind:
            "cover",

          label:
            "Cover",

          assetId:
            reader?.cover
              ?.available ===
              true
              ? clean(
                  reader
                    .cover
                    .assetId
                )
              : "",

          unit:
            null,
        },

        ...units.map(
          (
            unit,
            index
          ) => ({
            kind:
              "unit",

            label:
              pageLabels[
                index
              ] ||
              "Page",

            assetId:
              unit
                ?.illustration
                ?.available ===
                true
                ? clean(
                    unit
                      .illustration
                      .assetId
                  )
                : "",

            unit,
          })
        ),

        ...(
          backCoverAvailable &&
          backCoverUrl
            ? [
                {
                  kind:
                    "back_cover",

                  label:
                    "Back Cover",

                  assetId:
                    "",

                  unit:
                    null,
                },
              ]
            : []
        ),
      ],
      [
        reader,
        units,
        pageLabels,
        backCoverAvailable,
        backCoverUrl,
      ]
    );


  useEffect(
    () => {
      if (
        activeIndex >=
        items.length
      ) {
        setActiveIndex(0);
      }
    },
    [
      activeIndex,
      items.length,
    ]
  );

  const activeItem =
    items[
      activeIndex
    ] || null;

  useEffect(
    () => {
      let cancelled =
        false;

      let objectUrl =
        "";

      async function loadImage() {
        setImageUrl("");
        setImageError("");

        const assetId =
          clean(
            activeItem
              ?.assetId
          );

        if (
          !workId ||
          !assetId
        ) {
          setImageLoading(
            false
          );

          return;
        }

        setImageLoading(
          true
        );

        try {
          const response =
            await fetch(
              readerAssetUrl(
                workId,
                assetId
              ),
              {
                method:
                  "GET",

                credentials:
                  "include",

                headers: {
                  Accept:
                    "image/*",
                },
              }
            );

          if (
            !response.ok
          ) {
            throw new Error(
              "Image unavailable"
            );
          }

          const blob =
            await response.blob();

          if (
            cancelled
          ) {
            return;
          }

          objectUrl =
            URL.createObjectURL(
              blob
            );

          setImageUrl(
            objectUrl
          );
        } catch {
          if (
            !cancelled
          ) {
            setImageError(
              "This image is not available."
            );
          }
        } finally {
          if (
            !cancelled
          ) {
            setImageLoading(
              false
            );
          }
        }
      }

      loadImage();

      return () => {
        cancelled =
          true;

        if (
          objectUrl &&
          typeof URL !==
            "undefined" &&
          typeof URL.revokeObjectURL ===
            "function"
        ) {
          URL.revokeObjectURL(
            objectUrl
          );
        }
      };
    },
    [
      activeItem,
      workId,
    ]
  );

  function leaveReader() {
    if (
      typeof onNavigatePage ===
      "function"
    ) {
      onNavigatePage(
        "storylines"
      );

      return;
    }

    window.location.href =
      "/storylines";
  }

  if (loading) {
    return (
      <main
        className="ns-page"
        style={{
          maxWidth: 980,
          margin: "0 auto",
        }}
      >
        <div className="ns-card">
          Loading published book…
        </div>
      </main>
    );
  }

  if (
    error ||
    !reader
  ) {
    return (
      <main
        className="ns-page"
        style={{
          maxWidth: 760,
          margin: "0 auto",
        }}
      >
        <div className="ns-card">
          <h1
            className="ns-page-title"
          >
            Published book unavailable
          </h1>

          <p
            className="ns-page-subtitle"
          >
            {error ||
              "This book cannot be opened in Reader right now."}
          </p>

          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={
              leaveReader
            }
          >
            Back to NextScenes
          </button>
        </div>
      </main>
    );
  }

  const manuscript =
    activeItem
      ?.unit
      ?.manuscript ||
    {};

  const publishedDate =
    formattedDate(
      reader
        ?.publication
        ?.publishedAt
    );

  const preview =
    reader?.preview &&
    typeof reader.preview === "object"
      ? reader.preview
      : null;

  const previewAvailablePages =
    Number.isFinite(
      Number(
        preview?.availablePages
      )
    )
      ? Number(
          preview.availablePages
        )
      : 0;

  const previewTotalPages =
    Number.isFinite(
      Number(
        preview?.totalPages
      )
    )
      ? Number(
          preview.totalPages
        )
      : 0;

  const previewTruncated =
    preview?.mode === "limited" &&
    preview?.truncated === true &&
    previewTotalPages >
      previewAvailablePages;

  const activeUnitHasIllustration =
    activeItem?.kind ===
      "unit" &&
    Boolean(
      clean(
        activeItem?.assetId
      )
    );

  const canPrevious =
    activeIndex > 0;

  const canNext =
    activeIndex <
    items.length - 1;

  return (
    <main
      className="ns-page"
      data-testid="childrens-book-reader"
      style={{
        maxWidth: 1040,
        margin: "0 auto",
      }}
    >
      <header
        className="ns-card"
        style={{
          marginBottom: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "flex-start",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                textTransform:
                  "uppercase",
                letterSpacing:
                  "0.06em",
                color: "#64748b",
              }}
            >
              Published on NextScenes
            </div>

            <h1
              className="ns-page-title"
              style={{
                marginBottom: 6,
              }}
            >
              {clean(
                reader.title
              ) ||
                "Untitled Children’s Book"}
            </h1>

            <div
              className="ns-page-subtitle"
            >
              {clean(
                reader
                  ?.creator
                  ?.name
              )
                ? `by ${clean(
                    reader
                      .creator
                      .name
                  )}`
                : ""}

              {publishedDate
                ? ` · ${publishedDate}`
                : ""}
            </div>
          </div>

          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={
              leaveReader
            }
          >
            Exit Reader
          </button>
        </div>
      </header>

      <section
        className="ns-card"
        data-testid="childrens-book-reader-page-frame"
        style={{
          padding: 20,
          minHeight:
            "min(760px, 76vh)",
          display:
            "flex",
          flexDirection:
            "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <strong>
            {activeItem?.label ||
              "Book"}
          </strong>

          <span
            style={{
              color: "#64748b",
              fontSize: 13,
            }}
          >
            {activeIndex + 1} /{" "}
            {items.length}
            {preview?.mode === "limited" &&
            previewTotalPages > 0
              ? ` · Preview: ${previewAvailablePages} of ${previewTotalPages} pages`
              : ""}
          </span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              (
                activeItem?.kind ===
                  "cover" ||
                activeItem?.kind ===
                  "back_cover" ||
                (
                  activeItem?.kind ===
                    "unit" &&
                  !activeUnitHasIllustration
                )
              )
                ? "minmax(0, 1fr)"
                : "repeat(auto-fit, minmax(280px, 1fr))",
            gap:
              activeItem?.kind ===
                "unit" &&
              !activeUnitHasIllustration
                ? 0
                : 24,
            alignItems: "start",
            alignContent:
              "start",
            flex:
              "1 1 auto",
          }}
        >
          {activeItem?.kind ===
          "back_cover" ? (
            <div
              style={{
                minWidth: 0,
              }}
            >
              <iframe
                src={
                  backCoverUrl
                }
                title={`Back cover of ${clean(
                  reader.title
                ) ||
                  "published book"}`}
                style={{
                  display:
                    "block",
                  width:
                    "min(100%, 420px)",
                  height:
                    "min(72vh, 580px)",
                  margin:
                    "0 auto",
                  border:
                    "1px solid #e2e8f0",
                  borderRadius:
                    16,
                  background:
                    "#f8fafc",
                }}
              />
            </div>
          ) : (
          <div>
            {imageLoading ? (
              <div
                style={{
                  minHeight: 320,
                  display: "grid",
                  placeItems:
                    "center",
                  border:
                    "1px solid #e2e8f0",
                  borderRadius: 16,
                  background:
                    "#f8fafc",
                }}
              >
                Loading image…
              </div>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={
                  activeItem
                    ?.kind ===
                  "cover"
                    ? `Cover of ${clean(
                        reader.title
                      )}`
                    : "Book illustration"
                }
                style={{
                  display: "block",
                  width:
                    activeItem?.kind ===
                      "cover"
                      ? "min(100%, 340px)"
                      : "100%",
                  maxHeight:
                    activeItem?.kind ===
                      "cover"
                      ? 500
                      : 520,
                  margin:
                    activeItem?.kind ===
                      "cover"
                      ? "0 auto"
                      : 0,
                  objectFit:
                    "contain",
                  borderRadius: 16,
                  background:
                    "#f8fafc",
                }}
              />
            ) : (
              <div
                style={{
                  minHeight:
                    activeItem?.kind ===
                    "cover"
                      ? 320
                      : 180,
                  display:
                    activeItem?.kind ===
                      "unit"
                      ? "none"
                      : "grid",
                  placeItems:
                    "center",
                  border:
                    "1px solid #e2e8f0",
                  borderRadius: 16,
                  background:
                    "#f8fafc",
                  color: "#64748b",
                  textAlign:
                    "center",
                  padding: 20,
                }}
              >
                {imageError ||
                  (
                    activeItem?.kind ===
                    "cover"
                      ? "No cover image available."
                      : ""
                  )}
              </div>
            )}
          </div>
          )}

          {activeItem?.kind ===
          "unit" ? (
            <article
              style={{
                minWidth: 0,
                width:
                  "100%",
                maxWidth:
                  activeUnitHasIllustration
                    ? "none"
                    : 760,
                margin:
                  activeUnitHasIllustration
                    ? 0
                    : "0 auto",
                fontSize: 17,
                lineHeight: 1.7,
              }}
            >
              <ChildrensBookManuscriptReadOnly
                content={
                  manuscript.content
                }
                fallbackText={
                  manuscript.text ||
                  ""
                }
                lang={
                  clean(
                    reader.language
                  )
                    .toLowerCase()
                    .startsWith(
                      "fr"
                    )
                    ? "fr"
                    : "en"
                }
              />
            </article>
          ) : null}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            gap: 12,
            marginTop:
              "auto",
            paddingTop:
              24,
          }}
        >
          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={
              !canPrevious
            }
            onClick={() =>
              setActiveIndex(
                (value) =>
                  Math.max(
                    0,
                    value - 1
                  )
              )
            }
          >
            Previous
          </button>

          <button
            type="button"
            className="ns-button ns-button-primary"
            disabled={!canNext}
            onClick={() =>
              setActiveIndex(
                (value) =>
                  Math.min(
                    items.length -
                      1,
                    value + 1
                  )
              )
            }
          >
            Next
          </button>
        </div>
        {previewTruncated &&
        activeItem?.kind ===
          "unit" &&
        activeIndex ===
          units.length ? (
          <div
            role="status"
            className="ns-card-meta"
            style={{
              marginTop: 18,
              padding: "14px 16px",
              borderRadius: 12,
              background: "#f8fafc",
              lineHeight: 1.55,
            }}
          >
            You’ve reached the end of this
            preview.{" "}
            {previewAvailablePages} of{" "}
            {previewTotalPages} pages are
            available in Reader View.
            {backCoverAvailable
              ? " The finished back cover is available next."
              : ""}
          </div>
        ) : null}

      </section>
    </main>
  );
}
